import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchReports, generateReport, type ReportListItem } from '../lib/queries-s5.js';
import { useAuth } from '../lib/auth.js';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from '../components/ui/index.js';

export function ReportsPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const canGenerate = hasPermission('report.manage');

  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setReports(await fetchReports());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const generate = async (kind: 'board_monthly' | 'operating_weekly') => {
    setGenerating(kind);
    try {
      const id = await generateReport(kind);
      toast.success('Report snapshot generated');
      navigate(`/reports/${id}`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHeader
        title="Board & Operating Reports"
        subtitle="Immutable snapshots: what the board saw is what is stored, never recomputed on read. Every figure inside carries its numerator, denominator, timeframe and exclusions."
        actions={
          canGenerate && (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                loading={generating === 'operating_weekly'}
                onClick={() => void generate('operating_weekly')}
              >
                Generate weekly
              </Button>
              <Button
                variant="primary"
                data-testid="generate-board-report"
                loading={generating === 'board_monthly'}
                onClick={() => void generate('board_monthly')}
              >
                Generate board report
              </Button>
            </div>
          )
        }
      />
      {error && <ErrorState description={error} onRetry={() => void load()} />}
      {loading ? (
        <LoadingState label="Loading reports…" />
      ) : reports.length === 0 ? (
        <EmptyState
          title="No reports yet"
          description="Generate one now, or wait for the worker's weekly/monthly schedule."
        />
      ) : (
        <TableContainer>
          <Table density="compact">
            <THead>
              <TR>
                <TH>Report</TH>
                <TH>Kind</TH>
                <TH>Period</TH>
                <TH>Generated</TH>
                <TH>Sources</TH>
              </TR>
            </THead>
            <TBody>
              {reports.map((r) => (
                <TR key={r.id}>
                  <TD>
                    <Link
                      to={`/reports/${r.id}`}
                      className="font-semibold text-link hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                    >
                      {r.title}
                    </Link>
                  </TD>
                  <TD>
                    <Badge tone="neutral">
                      {r.kind === 'board_monthly' ? 'board / monthly' : 'operating / weekly'}
                    </Badge>
                  </TD>
                  <TD className="text-xs">
                    {new Date(r.periodStart).toLocaleDateString()} –{' '}
                    {new Date(r.periodEnd).toLocaleDateString()}
                  </TD>
                  <TD className="text-xs">
                    {new Date(r.generatedAt).toLocaleString()} · {r.generatedBy}
                  </TD>
                  <TD>
                    {r.hasIncompleteSources ? (
                      <Badge tone="warning">incomplete sources flagged</Badge>
                    ) : (
                      <Badge tone="success">complete</Badge>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}
